import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { 
  Smartphone, 
  Film, 
  Clock, 
  Download, 
  FileText, 
  Zap,
  X
} from 'lucide-react';

const ProjectTypeModal = ({ isOpen, onClose, onSelectType }) => {
  if (!isOpen) return null;

  const projectTypes = [
    {
      id: 'short-form',
      title: 'Short Form Video',
      subtitle: 'Social Media Content',
      description: 'Perfect for TikTok, Instagram Reels, YouTube Shorts',
      features: [
        '15-30 seconds duration',
        'Quick AI edits',
        'Direct MP4 export',
        'Vertical/Square formats'
      ],
      icon: Smartphone,
      color: '#0569ff',
      recommended: true
    },
    {
      id: 'long-form',
      title: 'Long Form Video',
      subtitle: 'Professional Content',
      description: 'Weddings, corporate videos, documentaries, YouTube',
      features: [
        'Unlimited duration',
        'Timeline editing',
        'XML export for Premiere/DaVinci',
        'Professional workflows'
      ],
      icon: Film,
      color: '#6366f1'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">Choose Your Project Type</h2>
          <p className="text-gray-400">Select the type of video you want to create</p>
        </div>

        {/* Project Type Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {projectTypes.map((type) => {
            const IconComponent = type.icon;
            return (
              <Card 
                key={type.id}
                className="relative border-2 hover:border-blue-500 transition-all duration-200 cursor-pointer group transform hover:scale-105"
                style={{
                  backgroundColor: '#141414',
                  borderColor: type.recommended ? '#0569ff' : '#374151'
                }}
                onClick={() => onSelectType(type.id)}
              >
                {type.recommended && (
                  <div className="absolute -top-3 left-4">
                    <span className="bg-blue-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                      Recommended
                    </span>
                  </div>
                )}
                
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4 mb-4">
                    <div 
                      className="p-3 rounded-lg flex-shrink-0"
                      style={{ backgroundColor: `${type.color}20` }}
                    >
                      <IconComponent 
                        className="w-8 h-8" 
                        style={{ color: type.color }}
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-1">{type.title}</h3>
                      <p className="text-blue-400 text-sm font-medium mb-2">{type.subtitle}</p>
                      <p className="text-gray-400 text-sm">{type.description}</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {type.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full flex-shrink-0"></div>
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Button 
                    className="w-full mt-6 text-white font-medium hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: type.color }}
                  >
                    Create {type.title}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Close Button */}
        <div className="text-center">
          <Button 
            variant="ghost" 
            onClick={onClose}
            className="text-gray-400 hover:text-white hover:bg-gray-800"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProjectTypeModal;